import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Package, ArrowUp, ArrowDown, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", sku: "", category: "Kitchen", unit: "pcs", stock: 0, reorder_level: 0, cost_price: 0, vendor: "" };

export default function Inventory() {
    const { activeHotelId } = useAuth();
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(empty);
    const [move, setMove] = useState(null);
    const [moveForm, setMoveForm] = useState({ type: "in", quantity: 0, notes: "" });
    const [category, setCategory] = useState("all");

    const load = async () => {
        if (!activeHotelId) return;
        const { data } = await api.get(`/inventory/items?hotel_id=${activeHotelId}`);
        setItems(data);
    };

    useEffect(() => { load(); }, [activeHotelId]);

    const submit = async () => {
        try {
            await api.post("/inventory/items", { ...form, hotel_id: activeHotelId, stock: Number(form.stock), reorder_level: Number(form.reorder_level), cost_price: Number(form.cost_price) });
            toast.success("Item added");
            setOpen(false); setForm(empty); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const submitMove = async () => {
        try {
            await api.post("/inventory/movements", { item_id: move.id, type: moveForm.type, quantity: Number(moveForm.quantity), notes: moveForm.notes });
            toast.success("Stock updated");
            setMove(null); setMoveForm({ type: "in", quantity: 0, notes: "" }); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const cats = ["all", "Kitchen", "Bar", "Laundry", "Housekeeping", "Other"];
    const filtered = category === "all" ? items : items.filter(i => i.category === category);

    return (
        <AppShell title="Inventory" subtitle={`${items.length} items · ${items.filter(i => i.stock <= i.reorder_level).length} below reorder`}>
            <div className="flex justify-between mb-4">
                <div className="flex gap-2">
                    {cats.map(c => (
                        <button key={c} onClick={() => setCategory(c)}
                            className={`px-3 py-1.5 text-xs rounded-sm border ${category === c ? "bg-[#0A1C3E] text-white border-[#0A1C3E]" : "bg-white text-slate-700 border-slate-200"}`}
                            data-testid={`inv-cat-${c}`}>{c}</button>
                    ))}
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild><Button className="bg-[#0A1C3E] rounded-sm" data-testid="inv-add-btn"><Plus className="w-4 h-4 mr-1" /> Item</Button></DialogTrigger>
                    <DialogContent>
                        <DialogHeader><DialogTitle>New Inventory Item</DialogTitle></DialogHeader>
                        <div className="space-y-3">
                            <div><Label className="text-xs uppercase tracking-wider">Name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="inv-name" /></div>
                            <div className="grid grid-cols-2 gap-3">
                                <div><Label className="text-xs uppercase tracking-wider">Category</Label>
                                    <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                                        <SelectTrigger><SelectValue /></SelectTrigger>
                                        <SelectContent>{["Kitchen", "Bar", "Laundry", "Housekeeping", "Other"].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                                    </Select></div>
                                <div><Label className="text-xs uppercase tracking-wider">Unit</Label><Input value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })} /></div>
                                <div><Label className="text-xs uppercase tracking-wider">Opening Stock</Label><Input type="number" value={form.stock} onChange={e => setForm({ ...form, stock: e.target.value })} /></div>
                                <div><Label className="text-xs uppercase tracking-wider">Reorder Level</Label><Input type="number" value={form.reorder_level} onChange={e => setForm({ ...form, reorder_level: e.target.value })} /></div>
                                <div><Label className="text-xs uppercase tracking-wider">Cost Price</Label><Input type="number" value={form.cost_price} onChange={e => setForm({ ...form, cost_price: e.target.value })} /></div>
                                <div><Label className="text-xs uppercase tracking-wider">Vendor</Label><Input value={form.vendor} onChange={e => setForm({ ...form, vendor: e.target.value })} /></div>
                            </div>
                        </div>
                        <DialogFooter><Button onClick={submit} disabled={!form.name} className="bg-[#0A1C3E]" data-testid="inv-submit">Create</Button></DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            <Card className="border border-slate-200 rounded-md overflow-hidden shadow-none">
                <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider text-left">
                        <tr>
                            <th className="px-4 py-3">Item</th>
                            <th className="px-4 py-3">Category</th>
                            <th className="px-4 py-3">Stock</th>
                            <th className="px-4 py-3">Reorder</th>
                            <th className="px-4 py-3">Cost</th>
                            <th className="px-4 py-3 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map(i => (
                            <tr key={i.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`inv-row-${i.id}`}>
                                <td className="px-4 py-3">
                                    <div className="text-sm font-medium text-slate-900 flex items-center gap-2">
                                        {i.name}
                                        {i.stock <= i.reorder_level && <AlertTriangle className="w-3.5 h-3.5 text-orange-500" />}
                                    </div>
                                    <div className="text-xs text-slate-500">{i.unit}</div>
                                </td>
                                <td className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500">{i.category}</td>
                                <td className="px-4 py-3 text-sm font-mono font-semibold">{i.stock}</td>
                                <td className="px-4 py-3 text-sm font-mono text-slate-500">{i.reorder_level}</td>
                                <td className="px-4 py-3 text-sm font-mono">₹{i.cost_price}</td>
                                <td className="px-4 py-3 text-right">
                                    <Button size="sm" variant="outline" onClick={() => setMove(i)} className="h-7 text-xs" data-testid={`inv-move-${i.id}`}>Adjust</Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Card>

            <Dialog open={!!move} onOpenChange={o => !o && setMove(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Adjust Stock: {move?.name}</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div><Label className="text-xs uppercase tracking-wider">Type</Label>
                            <Select value={moveForm.type} onValueChange={v => setMoveForm({ ...moveForm, type: v })}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="in">Stock In (Received)</SelectItem>
                                    <SelectItem value="out">Stock Out</SelectItem>
                                    <SelectItem value="consumption">Consumption</SelectItem>
                                </SelectContent>
                            </Select></div>
                        <div><Label className="text-xs uppercase tracking-wider">Quantity</Label>
                            <Input type="number" value={moveForm.quantity} onChange={e => setMoveForm({ ...moveForm, quantity: e.target.value })} data-testid="move-qty" /></div>
                        <div><Label className="text-xs uppercase tracking-wider">Notes</Label>
                            <Input value={moveForm.notes} onChange={e => setMoveForm({ ...moveForm, notes: e.target.value })} /></div>
                    </div>
                    <DialogFooter><Button onClick={submitMove} className="bg-[#0A1C3E]" data-testid="move-submit">Save</Button></DialogFooter>
                </DialogContent>
            </Dialog>
        </AppShell>
    );
}
